import { addMinutes, isAfter, isBefore } from "date-fns";

import {
  AssessmentSettingsFragment,
  AssessmentType,
  ExamTiming,
  MarkingTool,
} from "@/generated/graphql";

//////////////////////////////////////////////////////////////////////////////
// Assignment Settings                                                         //
//////////////////////////////////////////////////////////////////////////////

type AssessmentSettings = Pick<
  AssessmentSettingsFragment,
  "draftEnabled" | "draftDueDate" | "assessmentType"
>;

/** Select if drafting is enabled. */
export function selectHasDrafting(settings: AssessmentSettings | null) {
  return settings
    ? !selectIsExam(settings) &&
        settings.draftEnabled == true &&
        settings.draftDueDate != null
    : false;
}

/**
 * Selects Draft Due Date from Assessment Settings accounting for whether
 * drafting is enabled.
 */
export function selectDraftDueDate(
  settings: AssessmentSettings | null
): string | null {
  if (!settings || !selectHasDrafting(settings)) return null;
  return settings.draftDueDate;
}

export function selectHasTimeLimit(
  assessmentSettings: Pick<
    AssessmentSettingsFragment,
    "timeLimitEnabled" | "timeLimit"
  > | null
): boolean {
  return (
    !!assessmentSettings &&
    assessmentSettings.timeLimitEnabled === true &&
    assessmentSettings.timeLimit != null &&
    assessmentSettings.timeLimit > 0
  );
}
export function selectAssessmentTimeLimit(
  assessmentSettings: Pick<
    AssessmentSettingsFragment,
    "timeLimit" | "timeLimitEnabled"
  > | null
): number | null {
  if (!assessmentSettings || assessmentSettings.timeLimitEnabled == false)
    return null;
  return assessmentSettings.timeLimit;
}

/**
 * @returns the feedback return date from the sheet.
 */
export function selectAssessmentFeedbackDate(
  settings: Pick<
    AssessmentSettingsFragment,
    "feedbackDate" | "feedbackDateDisabled" | "assessmentType"
  > | null
): string | null {
  if (!settings) return null;
  const { feedbackDate, feedbackDateDisabled } = settings;
  if (selectIsExam(settings)) {
    if (!feedbackDateDisabled) return feedbackDate;
    return null;
  }
  return feedbackDate;
}

//////////////////////////////////////////////////////////////////////////////
// Exam Settings                                                             //
//////////////////////////////////////////////////////////////////////////////

type ExamSettings = Pick<
  AssessmentSettingsFragment,
  | "examTiming"
  | "examReadingTime"
  | "examReadingTimeDisabled"
  | "examWritingTime"
  | "examStartDate"
  | "examEndDate"
>;

/** Select if Assessment was setup as Exam */
export function selectIsExam(
  settings: Pick<AssessmentSettingsFragment, "assessmentType">
): boolean {
  return settings.assessmentType === AssessmentType.Exam;
}

/**
 * Select if the exam is in progress right now.
 */
export function selectIsExamInProgress(settings: ExamSettings): boolean {
  const { examStartDate } = settings;

  const endDate = selectExamCloseDate(settings);
  if (!examStartDate || !endDate) return false;

  const now = new Date();
  return (
    isAfter(now, new Date(examStartDate)) && isBefore(now, new Date(endDate))
  );
}

/**
 * Select whether the exam has closed now.
 */
export function isExamClosed(settings: ExamSettings): boolean {
  const endDate = selectExamCloseDate(settings);
  if (!endDate) return false;
  return isAfter(new Date(), new Date(endDate));
}

/**
 * Select the exam close date from Assessment Settings.
 *
 * NOTE: does not comsider special considerations or extensions.
 *
 * @returns the ISO8601 date for when the exam will close submissions.
 */
export function selectExamCloseDate(
  assessmentSettings: ExamSettings
): string | null {
  const { examTiming, examEndDate, examStartDate, examWritingTime } =
    assessmentSettings;

  if (examTiming === ExamTiming.Live && examStartDate) {
    const readingTime = selectExamReadingTime(assessmentSettings);
    const totalTime = (readingTime || 0) + (examWritingTime || 0);
    return addMinutes(new Date(examStartDate), totalTime).toISOString();
  }

  return examEndDate;
}

/**
 * Select the exam reading time from Assessment Settings
 * if it is enabled, otherwise returns null.
 */
export function selectExamReadingTime(
  assessmentSettings: ExamSettings | null
): number | null {
  if (
    !assessmentSettings ||
    assessmentSettings.examReadingTimeDisabled === true
  )
    return null;
  return assessmentSettings.examReadingTime;
}

//////////////////////////////////////////////////////////////////////////////
// Marking Settings                                                         //
//////////////////////////////////////////////////////////////////////////////

export function isCadmusGraderEnabled(
  assessmentSettings: Pick<AssessmentSettingsFragment, "markingTool"> | null
) {
  return (
    !!assessmentSettings &&
    assessmentSettings.markingTool === MarkingTool.Cadmusgrader
  );
}

/**
 * Feedback Date Disabled is relevant only for Marking Tools other than Cadmus
 * Grader.
 */
export function isWrittenFeedbackDisabled(
  assessmentSettings: Pick<
    AssessmentSettingsFragment,
    "markingTool" | "feedbackDateDisabled"
  > | null
): boolean {
  return (
    !isCadmusGraderEnabled(assessmentSettings) &&
    assessmentSettings?.feedbackDateDisabled !== false
  );
}

//////////////////////////////////////////////////////////////////////////////
// Group Assessment Settings                                                //
//////////////////////////////////////////////////////////////////////////////

export function isGroupAssessment(
  assessmentSettings: Pick<
    AssessmentSettingsFragment,
    "groupAssessmentEnabled"
  > | null
): boolean {
  return (
    !!assessmentSettings && assessmentSettings.groupAssessmentEnabled === true
  );
}

export function maxGroupScore(
  settings: Pick<
    AssessmentSettingsFragment,
    "maxGrade" | "groupAssessmentEnabled" | "groupReportMaxGrade"
  > | null
) {
  const maxGrade = settings?.maxGrade || 0;
  return settings && settings.groupAssessmentEnabled
    ? (settings.groupReportMaxGrade ?? 0)
    : maxGrade;
}

export function maxIndividualScore(
  settings: Pick<
    AssessmentSettingsFragment,
    "maxGrade" | "individualReportEnabled" | "individualReportMaxGrade"
  > | null
) {
  const maxGrade = settings?.maxGrade || 0;
  return settings && settings.individualReportEnabled
    ? (settings.individualReportMaxGrade ?? 0)
    : maxGrade;
}

export function maxPeerEvalScore(
  settings: Pick<
    AssessmentSettingsFragment,
    "peerEvaluationEnabled" | "peerEvaluationMaxGrade"
  > | null
) {
  return settings && settings.peerEvaluationEnabled
    ? (settings.peerEvaluationMaxGrade ?? 0)
    : 0;
}

export function maxSelfEvalScore(
  settings: Pick<
    AssessmentSettingsFragment,
    "selfEvaluationEnabled" | "selfEvaluationMaxGrade"
  > | null
) {
  return settings && settings.selfEvaluationEnabled
    ? (settings.selfEvaluationMaxGrade ?? 0)
    : 0;
}
